import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

const SegmentsTab = ({
    segmentsApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onManageMembers,
    tabIndex = 6,
}) => {
    if (segmentsApi.error) return <ErrorState error={segmentsApi.error} onRetry={() => segmentsApi.refetch()} />;
    if (segmentsApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (!Array.isArray(segmentsApi.data) || segmentsApi.data.length === 0)
        return <EmptyState message="Aucun segment configuré" color={SECTION_COLORS.segments.main} />;

    const page = pages[tabIndex] ?? 0;
    const paginated = segmentsApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((segment) => {
                    const hasConditions = segment.is_dynamic && segment.conditions && Object.keys(segment.conditions).length > 0;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={segment.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.segments.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.segments.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.segments.main}30`,
                                },
                            }}>
                                <Tooltip title={segment.is_dynamic ? "Segment dynamique" : "Segment statique"} placement="left">
                                    <Chip
                                        label={segment.is_dynamic ? "Dynamique" : "Statique"}
                                        size="small"
                                        sx={{
                                            position: "absolute", top: 10, right: 10,
                                            fontWeight: 700, fontSize: 10,
                                            bgcolor: segment.is_dynamic ? "#f0f9ff" : "#f8fafc",
                                            color: segment.is_dynamic ? "#0369a1" : "#475569",
                                        }}
                                    />
                                </Tooltip>

                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={12}>
                                    <GroupWorkIcon sx={{ color: SECTION_COLORS.segments.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {segment.name}
                                    </Typography>
                                </Box>

                                <Typography fontSize={11} color="text.secondary" noWrap mb={1.5} sx={{ pl: 3.5 }}>
                                    {segment.description || "Aucune description"}
                                </Typography>

                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {hasConditions && (
                                        <Chip
                                            label="Conditions"
                                            size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fffbeb", color: "#92400e" }}
                                        />
                                    )}
                                    <Chip
                                        label={`Calculé: ${fmtDate(segment.last_computed_at)}`}
                                        size="small"
                                        sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fafbfd", color: "#64748b" }}
                                    />
                                </Box>

                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={segment.active} />
                                    <Box display="flex" gap={0.5}>
                                        {!segment.is_dynamic && onManageMembers && (
                                            <Tooltip title="Gérer les membres">
                                                <IconButton size="small" onClick={() => onManageMembers(segment)}
                                                    sx={{
                                                        color: SECTION_COLORS.segments.main,
                                                        bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                        "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                        width: 30, height: 30,
                                                    }}>
                                                    <PeopleAltIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(segment)}
                                                sx={{
                                                    color: SECTION_COLORS.segments.main,
                                                    bgcolor: `${SECTION_COLORS.segments.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}20` }, width: 30, height: 30,
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(segment)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(segment)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {segmentsApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={segmentsApi.data.length} page={page}
                    onPageChange={handlePageChange(tabIndex)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default SegmentsTab;
